import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, FileDown, Loader2, Rocket, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadReport } from "@/lib/purchases";
import { useApp } from "@/context/AppContext";

const useOrder = (orderId, enabled) => {
  const [order, setOrder] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!enabled || !orderId) return undefined;
    let alive = true, tries = 0, timer;
    const poll = () => api.get(`/orders/${orderId}/status`).then(({ data }) => {
      if (!alive) return;
      setOrder(data);
      if (data.status === "pending" && ++tries < 25) timer = setTimeout(poll, 2500);
      else if (data.status === "pending") setTimedOut(true);
    }).catch(() => alive && setOrder({ status: "missing" }));
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [orderId, enabled]);
  return [order, setOrder, timedOut];
};

const DfyForm = ({ order, setOrder }) => {
  const [domain, setDomain] = useState("");
  const [brand, setBrand] = useState(order.deployment?.brand_name || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/dfy`, { domain, brand_name: brand });
      setOrder({ ...order, deployment: data });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Couldn't save. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 text-left" data-testid="dfy-details-form">
      <label className="block text-xs font-semibold text-slate-300">Your custom domain
        <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="mybrand.com" required data-testid="dfy-domain-input"
          className="mt-1 h-10 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50" />
      </label>
      <label className="block text-xs font-semibold text-slate-300">Confirm brand name
        <input value={brand} onChange={(e) => setBrand(e.target.value)} required maxLength={60} data-testid="dfy-brand-input"
          className="mt-1 h-10 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50" />
      </label>
      <button type="submit" disabled={busy} className="iv-cta inline-flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold text-white disabled:opacity-60" data-testid="dfy-submit-button">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Queue my 48-hour launch
      </button>
    </form>
  );
};

const Paid = ({ order, setOrder }) => {
  const dep = order.deployment;
  if (order.product_key === "PDF_REPORT") return (
    <div className="space-y-3">
      <button type="button" onClick={() => downloadReport(order.evaluation_id).catch(() => toast.error("Download failed. Please retry."))}
        className="iv-cta inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-white" data-testid="return-download-pdf">
        <FileDown className="h-4 w-4" /> Download PDF
      </button>
      <p className="text-xs text-slate-400">You can re-download it any time from your report.</p>
    </div>
  );
  if (order.product_key === "DFY_WEBSITE_LAUNCH") return dep?.status === "awaiting_details" ? <DfyForm order={order} setOrder={setOrder} /> : (
    <p className="flex items-center justify-center gap-2 text-sm font-semibold text-emerald-200" data-testid="return-dfy-queued">
      <Rocket className="h-4 w-4" /> {dep?.status === "live" ? "Your website is live" : dep?.status === "in_progress" ? "DFY launch in progress" : "DFY launch queued"} · {dep?.desired_domain} · our team takes it from here.
    </p>
  );
  return <p className="text-sm text-slate-300">Your purchase is active.</p>;
};

export default function PaymentReturn() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { authChecked } = useApp();
  const cancelled = pathname.includes("cancel");
  const [order, setOrder, timedOut] = useOrder(params.get("order_id"), authChecked);
  const showCancelled = cancelled && order?.status !== "paid";
  const back = order?.evaluation_id ? `/?stage=${order.product_key === "DFY_WEBSITE_LAUNCH" ? 4 : 3}&evaluation=${order.evaluation_id}` : "/?stage=3";
  return (
    <main className="grid min-h-screen place-items-center bg-[#060913] px-4 py-10 text-center" data-testid="payment-return-page">
      <div className="iv-glass w-full max-w-md space-y-5 rounded-2xl p-6">
        {order?.test_mode && <span className="inline-flex rounded-full border border-amber-400/50 bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold tracking-wider text-amber-200" data-testid="return-test-mode-badge">TEST MODE</span>}
        {showCancelled ? (
          <p className="flex items-center justify-center gap-2 text-lg font-bold text-white" data-testid="return-cancelled"><XCircle className="h-5 w-5 text-rose-300" /> Checkout cancelled — nothing was charged.</p>
        ) : !order || (order.status === "pending" && !timedOut) ? (
          <p className="flex items-center justify-center gap-2 text-sm text-slate-300" data-testid="return-polling"><Loader2 className="h-4 w-4 animate-spin text-cyan-300" /> Confirming your payment…</p>
        ) : order.status === "paid" ? (
          <>
            <p className="flex items-center justify-center gap-2 text-lg font-bold text-white" data-testid="return-paid"><CheckCircle2 className="h-5 w-5 text-emerald-300" /> Payment confirmed · {order.product_name}</p>
            <Paid order={order} setOrder={setOrder} />
          </>
        ) : (
          <p className="text-sm text-rose-200" data-testid="return-failed">
            {order.status === "pending" ? "We're still waiting for the payment confirmation. Refresh this page in a minute." : order.status === "missing" ? "Order not found for this account." : `Payment ${order.status}. Nothing was unlocked.`}
          </p>
        )}
        <a href={back} className="inline-flex rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white" data-testid="return-back-link">Back to my workspace</a>
      </div>
    </main>
  );
}
